'use client';

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { Link } from 'react-router-dom';

import { useGlobal } from '@/contexts/GlobalContext';
import { useSdHeaderMenuDrawer } from '@/contexts/SdHeaderMenuDrawerContext';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import SdHeaderMenuDrawer from '@/components/layouts/SdHeaderMenuDrawer';
import TruncateText from '@/components/common/TruncateText';
import OwnerLabel from '@/components/teams/OwnerLabel';
import OBOGLabel from '@/components/teams/OBOGLabel';
import Tab from '@/components/layouts/Tab';
import { useCurrentTeamTabMenu } from '@/hooks/useCurrentTeamTabMenu';
import { Status } from '@/types/teams';
import { getFilePath } from '@/utils';
import { isTeamConfigInitialized } from '@/utils/teams';

/**
 * SDヘッダー
 */
export default function SdHeader() {
  /**
   * Next router
   */
  const params = useParams<{ teamuuid: string }>();

  /**
   * React context
   */
  const context = useGlobal();
  const { isOpen, openDrawer, closeDrawer } = useSdHeaderMenuDrawer();
  const isVisible = useScrollDirection();
  const { menuItems: teamMenuItems, activeIndex: teamActiveIndex } = useCurrentTeamTabMenu();

  /**
   * useState
   */
  const [showBadge, setShowBadge] = useState<boolean>(false);
  const [teamName, setTeamName] = useState<string>(context.team?.name || '');
  const [teamEmblem, setTeamEmblem] = useState<{ file_name: string; file_path: string } | undefined>(undefined);
  const [colorA, setColorA] = useState<string>(context.team?.color_a || '');
  const [colorB, setColorB] = useState<string>(context.team?.color_b || '');
  const [memberStatus, setMemberStatus] = useState<Status | undefined>(undefined);

  const isOGOB = () => {
    return memberStatus === Status.OBOG;
  };

  const getTeamEmblemPath = () => {
    // NOTE: チーム設定時、画像選択後のContext上書きでプレビュー画像を表示する考慮。
    // プレビュー時はfile_nameに"preview"を設定して判別する。
    if (teamEmblem?.file_name === 'preview') {
      return teamEmblem?.file_path;
    }
    return getFilePath(teamEmblem?.file_path);
  };

  /**
   * ドロワー表示（プロフィールカード表示）
   */
  const handleOpenDrawer = () => {
    openDrawer(<SdHeaderMenuDrawer />);
  };

  // チーム招待があるかどうか
  const hasTeamInvitation = (userTeams: typeof context.user_teams) => {
    return (userTeams || []).some((userTeam) => userTeam.status === Status.Requested);
  };

  // チームの初期設定が完了していないかを判定する関数(複数チームを持つ場合)
  const isNotTeamsConfigInitialized = (userTeams: typeof context.user_teams, userId: number | undefined): boolean => {
    return (userTeams ?? []).some(
      (userTeam) => userTeam.team.owner_id === userId && !isTeamConfigInitialized(userTeam.team)
    );
  };

  useEffect(() => {
    setShowBadge(
      hasTeamInvitation(context.user_teams) || isNotTeamsConfigInitialized(context.user_teams, context.user?.id)
    );
  }, [context.user_teams, context.user]);

  useEffect(() => {
    setTeamName(context.team?.name || '');
  }, [context.team?.name]);

  useEffect(() => {
    setColorA(context.team?.color_a || '');
  }, [context.team?.color_a]);

  useEffect(() => {
    setColorB(context.team?.color_b || '');
  }, [context.team?.color_b]);

  useEffect(() => {
    setTeamEmblem(context.team?.team_emblem || undefined);
  }, [context.team?.team_emblem]);

  useEffect(() => {
    // NOTE: フットボーラが所属してるチーム一覧から対象チームの所属ステータスを取得する
    const user_teams = context.user_teams?.find((ut) => ut.team.uuid === context.team?.uuid);
    setMemberStatus(user_teams?.status);
  }, [context.user_teams, context.team]);

  return (
    <header
      className={`
        relative transform transition-transform duration-300 ease-in-out
        ${isVisible ? 'translate-y-0' : '-translate-y-full'}
    `}>
      {/* ハンバーガーMENU */}
      <div className="absolute top-0 left-0 z-10 p-1">
        {!isOpen ? (
          <button
            type="button"
            onClick={handleOpenDrawer}
            className="relative bg-transparent hover:bg-gray-100 hover:bg-opacity-25 rounded-lg text-white text-sm w-8 h-8 flex items-center justify-center"
            style={{ color: `${colorB}` }}>
            <svg
              className="w-4 h-4"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 17 14">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M1 1h15M1 7h15M1 13h15"
              />
            </svg>
            <span className="sr-only">Open menu</span>
            {showBadge && (
              <div className="absolute top-1 right-0 w-3 h-3 rounded-full border border-white bg-blue-600"></div>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={closeDrawer}
            className="bg-transparent hover:bg-gray-100 hover:bg-opacity-25 rounded-lg text-sm w-8 h-8 flex items-center justify-center"
            style={{ color: `${colorB}` }}>
            <svg
              className="w-4 h-4"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
            <span className="sr-only">Close menu</span>
          </button>
        )}
      </div>
      <div
        className="relative flex justify-center items-center select-none px-2 py-2 h-10"
        style={{ background: `${colorA}` }}>
        {/* ロゴ */}
        {context.team ? (
          <div className="w-full flex items-center justify-center text-white p-8">
            <Link to={`/teams/${params.teamuuid}`} className="flex items-center">
              {teamEmblem && <img src={getTeamEmblemPath()} alt="teamEmblem" className="w-6 h-6 me-2 object-contain" />}
              <div
                className={`font-semibold
                ${teamName.length >= 1 && teamName.length <= 10 && ' text-lg'}
                ${teamName.length >= 11 && teamName.length <= 15 && ' text-base'}
                ${teamName.length >= 16 && ' text-sm'}
              `}
                style={{ color: `${colorB}` }}>
                <TruncateText text={teamName} maxLength={20} />
              </div>
            </Link>
          </div>
        ) : (
          <Link to="/teams" className="flex">
            <img src="/footballlog.svg" alt="logo" width={128} height={26} />
          </Link>
        )}
        <div className="absolute top-1/2 right-2 z-10 transform -translate-y-1/2 flex items-center">
          {context.isTeamOwner() && <OwnerLabel />}
          {isOGOB() && <OBOGLabel />}
        </div>
      </div>
      {/* レイアウト調整用 */}

      <Tab teamColor={context.team?.color_a} menuItems={teamMenuItems} activeIndex={teamActiveIndex} />
    </header>
  );
}
